import { useState, useEffect, useRef } from 'react';
// import { getProjects } from '../../services/projectService'; // Đã xóa module project
// @ts-ignore
import { getHeroForCustomer } from '../../services/heroService';
// @ts-ignore
import { getSeoForCustomer } from '../../services/settingService';
import { useFooter } from '../../contexts/FooterContext';
import { useNews } from '../../contexts/NewsContext';
import homeSectionService from '../../services/homeSectionService';
// @ts-ignore
import sectionItemService from '../../services/sectionItemService';
import type { HeroData, HeroButton, NewsItem, ServiceItem, TestimonialItem } from './components/types'; // ProjectItem đã xóa module project
import HeroSection from './components/HeroSection';
import SolutionsSection from './components/SolutionsSection';
import ServicesSection from './components/ServicesSection';
import ProcessSection from './components/ProcessSection';
import WhyChooseSection from './components/WhyChooseSection';
// import FeaturedProjectsSection from './components/FeaturedProjectsSection'; // Đã xóa module project
import TestimonialsSection from './components/TestimonialsSection';
import FeaturedNewsSection from './components/FeaturedNewsSection';
import BusinessChallengesSection from './components/BusinessChallengesSection';
import CTASection from '../../components/customer/CTASection';

interface SeoSettings {
  siteName: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroData, setHeroData] = useState<HeroData | null>(null);
  // const [projectsData, setProjectsData] = useState<ProjectItem[]>([]); // Đã xóa module project
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true); // Loading state riêng cho news
  const [homeSections, setHomeSections] = useState<Array<{
    id: string;
    name: string;
    type: string;
    description: string;
    background?: string;
    nameColor?: string;
    descriptionColor?: string;
    isVisible: boolean;
    order: number;
  }>>([]);
  
  // State để lưu section items từ API (tối ưu - chỉ call 1 lần)
  const [sectionItems, setSectionItems] = useState<{
    businessChallenges?: any[];
    whyChoose?: any[];
    process?: any[];
    solutions?: any[];
    services?: any[];
    benefits?: any[];
  }>({});
  // Refs for scroll animations
  const solutionsRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);
  const whyChooseRef = useRef<HTMLElement>(null);
  // const projectsRef = useRef<HTMLElement>(null); // Đã xóa module project
  const testimonialsRef = useRef<HTMLElement>(null);
  const newsRef = useRef<HTMLElement>(null);
  const businessChallengesRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  const [visibleSections, setVisibleSections] = useState({
    solutions: false, // Bắt đầu false để animation hoạt động khi scroll
    services: false,
    process: false,
    whyChoose: false,
    // projects: false, // Đã xóa module project
    testimonials: false,
    news: false,
    businessChallenges: false,
    cta: false
  });

  const [seoSettings, setSeoSettings] = useState<SeoSettings | null>(null);
  const { footerData } = useFooter();
  const { featuredNews: featuredNewsFromContext } = useNews();

  // Scroll to top on mount (when page loads/reloads)
  useEffect(() => {
    // Force scroll to top
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Scroll multiple times to override browser restore scroll position
    requestAnimationFrame(() => {
      scrollToTop();
      requestAnimationFrame(() => {
        scrollToTop();
        setTimeout(() => {
          scrollToTop();
        }, 50);
        setTimeout(() => {
          scrollToTop();
        }, 150);
        setTimeout(() => {
          scrollToTop();
        }, 300);
        setTimeout(() => {
          scrollToTop();
        }, 500);
      });
    });
  }, []);

  // Fetch SEO settings and update document meta tags
  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const response = await getSeoForCustomer();
        if (response.success && response.data) {
          setSeoSettings(response.data);
          
          // Update document title
          if (response.data.metaTitle) {
            document.title = response.data.metaTitle;
          }
          
          // Update meta description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription && response.data.metaDescription) {
            metaDescription.setAttribute('content', response.data.metaDescription);
          }
          
          // Update meta keywords
          let metaKeywords = document.querySelector('meta[name="keywords"]');
          if (metaKeywords && response.data.metaKeywords) {
            metaKeywords.setAttribute('content', response.data.metaKeywords);
          }
          
          // Update Open Graph tags
          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle && response.data.metaTitle) {
            ogTitle.setAttribute('content', response.data.metaTitle);
          }
          
          const ogDescription = document.querySelector('meta[property="og:description"]');
          if (ogDescription && response.data.metaDescription) {
            ogDescription.setAttribute('content', response.data.metaDescription);
          }
          
          // Update Twitter Card tags
          const twitterTitle = document.querySelector('meta[name="twitter:title"]');
          if (twitterTitle && response.data.metaTitle) {
            twitterTitle.setAttribute('content', response.data.metaTitle);
          }
          
          const twitterDescription = document.querySelector('meta[name="twitter:description"]');
          if (twitterDescription && response.data.metaDescription) {
            twitterDescription.setAttribute('content', response.data.metaDescription);
          }
          
          // Meta robots (index, follow)
          let metaRobots = document.querySelector('meta[name="robots"]');
          if (!metaRobots) {
            metaRobots = document.createElement('meta');
            metaRobots.setAttribute('name', 'robots');
            document.head.appendChild(metaRobots);
          }
          metaRobots.setAttribute('content', response.data.robots || 'index, follow');
          
          // Canonical URL
          const canonicalUrl = response.data.canonicalUrl || window.location.origin;
          let linkCanonical = document.querySelector('link[rel="canonical"]');
          if (!linkCanonical) {
            linkCanonical = document.createElement('link');
            linkCanonical.setAttribute('rel', 'canonical');
            document.head.appendChild(linkCanonical);
          }
          linkCanonical.setAttribute('href', canonicalUrl);
        }
      } catch (err) {
        console.error('Error fetching SEO settings:', err);
      }
    };
    
    fetchSeoSettings();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch hero section, home sections, và section items (tối ưu - chỉ 1 API call)
        // News đã được fetch ở Header và share qua NewsContext, không cần call lại
        // Projects đã bị xóa module
        const [heroResponse, sectionsResponse, sectionItemsResponse] = await Promise.all([
          getHeroForCustomer('home'),
          homeSectionService.getVisibleForCustomer(),
          sectionItemService.getHomepage()
        ]);
        
        if (heroResponse.success && heroResponse.data) {
          // Đảm bảo isActive mặc định là true nếu không có trong response
          const heroData = {
            ...heroResponse.data,
            isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
            buttons: heroResponse.data.buttons || []
          };
          setHeroData(heroData);
        }
        
        // Projects đã bị xóa module - không cần xử lý
        
        // News data sẽ được xử lý riêng trong useEffect khác để watch context changes
        
        // Xử lý home sections response
        if (sectionsResponse?.success && sectionsResponse.data && sectionsResponse.data.length > 0) {
          setHomeSections(sectionsResponse.data);
        } else {
          // Fallback: nếu không có sections từ admin, hiển thị tất cả sections mặc định
          setHomeSections([
            { id: 'default-service', type: 'service', name: 'Dịch vụ', description: '', background: '', isVisible: true, order: 1 },
            { id: 'default-process', type: 'process', name: 'Quy trình', description: '', background: '', isVisible: true, order: 2 },
            { id: 'default-whyChoose', type: 'whyChoose', name: 'Tại sao chọn', description: '', background: '', isVisible: true, order: 3 },
            // { id: 'default-project', type: 'project', name: 'Dự án', description: '', background: '', isVisible: true, order: 4 }, // Đã xóa module project
            { id: 'default-testimonials', type: 'testimonials', name: 'Testimonials', description: '', background: '', isVisible: true, order: 5 },
            { id: 'default-news', type: 'news', name: 'Tin tức', description: '', background: '', isVisible: true, order: 6 }
          ]);
        }
        
        // Xử lý section items response (tối ưu - chỉ 1 API call)
        if (sectionItemsResponse?.success && sectionItemsResponse.data) {
          setSectionItems(sectionItemsResponse.data);
        } else {
          setSectionItems({});
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Có lỗi xảy ra');
        // setProjectsData([]); // Đã xóa module project
        setNewsData([]);
        // Fallback khi có lỗi
        setHomeSections([
          { id: 'default-service', type: 'service', name: 'Dịch vụ', description: '', background: '', isVisible: true, order: 1 },
          { id: 'default-process', type: 'process', name: 'Quy trình', description: '', background: '', isVisible: true, order: 2 },
          { id: 'default-whyChoose', type: 'whyChoose', name: 'Tại sao chọn', description: '', background: '', isVisible: true, order: 3 },
          { id: 'default-project', type: 'project', name: 'Dự án', description: '', background: '', isVisible: true, order: 4 },
          { id: 'default-testimonials', type: 'testimonials', name: 'Testimonials', description: '', background: '', isVisible: true, order: 5 },
          { id: 'default-news', type: 'news', name: 'Tin tức', description: '', background: '', isVisible: true, order: 6 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Watch context changes để update news data khi Header fetch xong
  useEffect(() => {
    // Nếu context đã có data, map và set vào state
    if (featuredNewsFromContext && featuredNewsFromContext.length > 0) {
      // Map data từ context sang format của NewsItem trong HomePage
      const mappedNews = featuredNewsFromContext.map((news: any) => ({
        _id: news._id,
        title: news.title,
        slug: news.slug,
        excerpt: news.excerpt || news.description || '',
        description: news.description || '',
        featuredImage: news.featuredImage || news.image || '',
        image: news.image || news.featuredImage || '',
        category: news.category,
        newCategory: news.category || news.newCategory,
        newCategoryId: news.category || news.newCategoryId,
        categoryName: news.categoryName || news.category?.name || '',
        author: news.author || '',
        readTime: news.readTime || '',
        tags: news.tags || [],
        createdAt: news.createdAt || '',
        updatedAt: news.updatedAt || ''
      }));
      setNewsData(mappedNews);
      setNewsLoading(false);
    } else if (featuredNewsFromContext && Array.isArray(featuredNewsFromContext) && featuredNewsFromContext.length === 0) {
      // Context đã được set nhưng là empty array (đã fetch xong nhưng không có data)
      setNewsData([]);
      setNewsLoading(false);
    }
    // Nếu featuredNewsFromContext là undefined/null, có nghĩa là Header chưa fetch xong
    // Giữ loading state và đợi context được update
  }, [featuredNewsFromContext]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation trigger on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Scroll animation observer - cần chạy lại khi homeSections thay đổi
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute('data-section');
          if (sectionName) {
            setVisibleSections(prev => ({ ...prev, [sectionName]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Set timeout để đảm bảo DOM đã render xong (tăng timeout khi sections render động)
    const timeoutId = setTimeout(() => {
      const refs = [
        { ref: solutionsRef, name: 'solutions' },
        { ref: servicesRef, name: 'services' },
        { ref: processRef, name: 'process' },
        { ref: whyChooseRef, name: 'whyChoose' },
        // { ref: projectsRef, name: 'projects' }, // Đã xóa module project
        { ref: testimonialsRef, name: 'testimonials' },
        { ref: newsRef, name: 'news' },
        { ref: businessChallengesRef, name: 'businessChallenges' },
        { ref: ctaRef, name: 'cta' }
      ];

      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.observe(ref.current);
        }
      });
    }, 300); // Tăng timeout để đảm bảo sections động đã render xong

    return () => {
      clearTimeout(timeoutId);
      const refs = [
        solutionsRef, servicesRef, processRef, whyChooseRef, // projectsRef, // Đã xóa module project 
        testimonialsRef, newsRef, businessChallengesRef, ctaRef
      ];
      refs.forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [homeSections]); // Thêm dependency để chạy lại khi sections thay đổi

  const services: ServiceItem[] = [
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Migration',
      description: 'Chuyển đổi hạ tầng lên cloud với AWS, Azure, Google Cloud. Tối ưu chi phí và hiệu suất vận hành.'
    },
    {
      icon: 'ri-brain-line',
      title: 'AI Development',
      description: 'Phát triển giải pháp AI/ML tùy chỉnh cho doanh nghiệp. Computer Vision, NLP, và Automation.'
    },
    {
      icon: 'ri-code-box-line',
      title: 'Custom Software',
      description: 'Phát triển phần mềm theo yêu cầu. Web, Mobile, Enterprise với công nghệ tiên tiến nhất.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity',
      description: 'Bảo mật toàn diện cho hệ thống. Đánh giá, tư vấn và triển khai giải pháp an ninh mạng.'
    },
    {
      icon: 'ri-git-merge-line',
      title: 'DevOps Solutions',
      description: 'Tự động hóa quy trình phát triển và triển khai. CI/CD, Infrastructure as Code, Monitoring.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Data Analytics',
      description: 'Phân tích dữ liệu thông minh. Business Intelligence, Big Data, và Data Visualization.'
    }
  ];

  const testimonials: TestimonialItem[] = [
    {
      rating: 5,
      quote: 'StackWay đã giúp chúng tôi chuyển đổi số hoàn toàn. Hệ thống vận hành mượt mà, hiệu quả tăng 300%.',
      avatar: 'https://readdy.ai/api/search-image?query=professional-vietnamese-business-executive-portrait-in-modern-office-wearing-formal-suit-confident-smile-clean-white-background-high-quality-corporate-headshot&width=80&height=80&seq=testimonial1&orientation=squarish',
      name: 'Nguyễn Văn An',
      position: 'CEO',
      company: 'TechViet Group'
    },
    {
      rating: 5,
      quote: 'Đội ngũ chuyên nghiệp, tận tâm. Dự án hoàn thành đúng tiến độ với chất lượng vượt mong đợi.',
      avatar: 'https://readdy.ai/api/search-image?query=professional-vietnamese-female-business-leader-portrait-elegant-business-attire-warm-smile-clean-white-background-corporate-photography&width=80&height=80&seq=testimonial2&orientation=squarish',
      name: 'Trần Thị Bình',
      position: 'CTO',
      company: 'Digital Solutions JSC'
    },
    {
      rating: 5,
      quote: 'Giải pháp AI của StackWay giúp chúng tôi tự động hóa 80% quy trình, tiết kiệm hàng tỷ đồng mỗi năm.',
      avatar: 'https://readdy.ai/api/search-image?query=professional-vietnamese-technology-director-portrait-modern-business-casual-friendly-expression-clean-white-background-high-quality-headshot&width=80&height=80&seq=testimonial3&orientation=squarish',
      name: 'Lê Minh Cường',
      position: 'Giám đốc Công nghệ',
      company: 'SmartRetail Vietnam'
    }
  ];

  const handleButtonClick = (button: HeroButton) => {
    if (button.actionType === 'modal') {
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <HeroSection
        heroData={heroData}
        isVisible={isVisible}
        scrolled={scrolled}
        onButtonClick={handleButtonClick}
      />

      {/* Render sections theo thứ tự từ admin */}
      {homeSections.length > 0 ? (
        homeSections.map(section => {
          const sectionType = section.type;
        
        switch (sectionType) {
          case 'solutions':
            return (
              <SolutionsSection
                key={section.id}
                isVisible={visibleSections.solutions}
                sectionRef={solutionsRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
              />
            );
          case 'service':
            return (
              <ServicesSection
                key={section.id}
                services={services}
                isVisible={visibleSections.services}
                sectionRef={servicesRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
              />
            );
          case 'process':
            return (
              <ProcessSection
                key={section.id}
                isVisible={visibleSections.process}
                sectionRef={processRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
                items={sectionItems.process}
              />
            );
          case 'whyChoose':
            return (
              <WhyChooseSection
                key={section.id}
                isVisible={visibleSections.whyChoose}
                sectionRef={whyChooseRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
                items={sectionItems.whyChoose}
              />
            );
          // case 'project': // Đã xóa module project
          //   return (
          //     <FeaturedProjectsSection
          //       key={section.id}
          //       projectsData={projectsData}
          //       loading={loading}
          //       isVisible={visibleSections.projects}
          //       sectionRef={projectsRef}
          //       name={section.name}
          //       description={section.description}
          //       background={section.background}
          //       nameColor={section.nameColor}
          //       descriptionColor={section.descriptionColor}
          //     />
          //   );
          case 'testimonials':
            return (
              <TestimonialsSection
                key={section.id}
                testimonials={testimonials}
                activeTestimonial={activeTestimonial}
                onTestimonialChange={setActiveTestimonial}
                isVisible={visibleSections.testimonials}
                sectionRef={testimonialsRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
              />
            );
          case 'news':
            return (
              <FeaturedNewsSection
                key={section.id}
                newsData={newsData}
                loading={newsLoading}
                isVisible={visibleSections.news}
                sectionRef={newsRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
              />
            );
          case 'businessChallenges':
            return (
              <BusinessChallengesSection
                key={section.id}
                isVisible={visibleSections.businessChallenges}
                sectionRef={businessChallengesRef}
                name={section.name}
                description={section.description}
                background={section.background}
                nameColor={section.nameColor}
                descriptionColor={section.descriptionColor}
                items={sectionItems.businessChallenges}
              />
            );
          default:
            return null;
        }
        })
      ) : (
        // Fallback khi chưa có sections
        <>
          <ServicesSection
            services={services}
            isVisible={visibleSections.services}
            sectionRef={servicesRef}
          />
          <BusinessChallengesSection
            isVisible={visibleSections.businessChallenges}
            sectionRef={businessChallengesRef}
            items={sectionItems.businessChallenges}
          />
          <ProcessSection
            isVisible={visibleSections.process}
            sectionRef={processRef}
            items={sectionItems.process}
          />
          <WhyChooseSection
            isVisible={visibleSections.whyChoose}
            sectionRef={whyChooseRef}
            items={sectionItems.whyChoose}
          />
          {/* <FeaturedProjectsSection // Đã xóa module project
            projectsData={projectsData}
            loading={loading}
            isVisible={visibleSections.projects}
            sectionRef={projectsRef}
          /> */}
          <TestimonialsSection
            testimonials={testimonials}
            activeTestimonial={activeTestimonial}
            onTestimonialChange={setActiveTestimonial}
            isVisible={visibleSections.testimonials}
            sectionRef={testimonialsRef}
          />
          <FeaturedNewsSection
            newsData={newsData}
            loading={newsLoading}
            isVisible={visibleSections.news}
            sectionRef={newsRef}
          />
        </>
      )}

      <CTASection
        isVisible={visibleSections.cta}
        sectionRef={ctaRef}
        phone={footerData?.footer?.contact?.phone || '1900-xxxx'}
      />
    </div>
  );
}
